import { StatusIcon, type IconKind } from '../../components/StatusIcon';
import type { CriticalFilesResult } from '../../lib/runAssets';
import type { UseAutoAnalyze } from '../tests-wizard/useAutoAnalyze';
import { useRunStore, type AnalysisInputs } from '../../state/runStore';
import type { VideoAnalysisProvider } from '@sr-test-wizard/shared';

interface Phase3ChipProps {
  status: IconKind;
  label: string;
  detail: string;
  onClick?: () => void;
  title?: string;
}

function Phase3Chip({ status, label, detail, onClick, title }: Phase3ChipProps) {
  const className = `phase3-toolbar-chip phase3-toolbar-chip-${status} ${onClick ? 'is-clickable' : ''}`;
  const inner = (
    <>
      <StatusIcon status={status} size={14} />
      <span className="phase3-toolbar-chip-label">{label}</span>
      <span className="phase3-toolbar-chip-detail">{detail}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} title={title}>
        {inner}
      </button>
    );
  }
  return (
    <span className={className} title={title}>
      {inner}
    </span>
  );
}

interface Props {
  criticalFiles: CriticalFilesResult | null;
  imagesCount: number | null;
  imagesReady: boolean;
  drivePhaseFailed: boolean;
  analysisExists: boolean;
  /** True when at least one `video-analysis.<slug>.json` exists on disk. */
  videoAnalysisExists: boolean;
  totalTests: number;
  unanalyzed: number;
  scored: number;
  filteredScored: number;
  filteredCount: number;
  resetBusy: boolean;
  loadTestsCompleted: boolean;
  reloadBusy: boolean;
  onReloadTests: () => void;
  onResetAll: () => void;
  onResetFiltered: () => void;
  onOpenGallery: () => void;
  onGoToImagesPhase: () => void;
  onGoToVideosPage: () => void;
  autoAnalyze: UseAutoAnalyze;
}

/**
 * Sticky one-row top bar for Phase 5 (AI Validation). Replaces the original
 * 4 stacked cards (header + 3 banners + reload card + AutoAnalyzeBar) with a
 * single ~52px strip so the wizard claims the rest of the viewport. Every
 * piece of info from the old layout is preserved: chip text + tooltips for
 * status detail, a clickable Screenshots chip that opens the gallery (or
 * jumps to Phase 4), and the Reload + auto-analyze controls collapsed into
 * a right-aligned action cluster.
 *
 * NOTE: the file/component name is still `Phase3Toolbar` for git-history
 * reasons; the visible step number is now 5.
 */
export function Phase3Toolbar({
  criticalFiles,
  imagesCount,
  imagesReady,
  drivePhaseFailed,
  analysisExists,
  videoAnalysisExists,
  totalTests,
  unanalyzed,
  scored,
  filteredScored,
  filteredCount,
  resetBusy,
  loadTestsCompleted,
  reloadBusy,
  onReloadTests,
  onResetAll,
  onResetFiltered,
  onOpenGallery,
  onGoToImagesPhase,
  onGoToVideosPage,
  autoAnalyze,
}: Props) {
  const criticalStatus: IconKind =
    criticalFiles && criticalFiles.total > 0
      ? criticalFiles.missing === 0 && criticalFiles.duplicates === 0
        ? 'completed'
        : 'failed'
      : 'pending';
  const criticalDetail = criticalFiles && criticalFiles.total > 0
    ? `${criticalFiles.found}/${criticalFiles.total}${criticalFiles.missing > 0 ? ` - ${criticalFiles.missing} missing` : ''}`
    : '0';
  const criticalTooltip =
    criticalFiles && criticalFiles.total > 0
      ? criticalFiles.missing > 0 || criticalFiles.duplicates > 0
        ? `${criticalFiles.found} found, ${criticalFiles.missing} missing of ${criticalFiles.total} — needs attention.`
        : `${criticalFiles.found} found of ${criticalFiles.total}.`
      : 'Load critical files in Phase 3 (TEXEC description or manifest).';

  const screenshotsStatus: IconKind = imagesReady
    ? 'completed'
    : drivePhaseFailed
      ? 'failed'
      : 'pending';
  const screenshotsDetail = imagesReady ? `${imagesCount ?? 0}` : 'pending';
  const screenshotsTooltip = analysisExists
    ? 'Vision analysis cached - click to open the gallery.'
    : imagesReady
      ? 'Run "Analyze Images" in Phase 4 to generate the vision summary.'
      : 'Open Phase 4 to download screenshots from Drive.';

  const { running, progress, error, start, stop } = autoAnalyze;
  const analyzingTestKeys = useRunStore((s) => s.analyzingTestKeys);
  const currentlyAnalyzing = analyzingTestKeys[0] ?? null;
  const testsBusy = running || analyzingTestKeys.length > 0;
  // Spinner only while analyze-all (or a single test) is actually running;
  // unscored-but-idle is paused (amber), not blue "in progress".
  const testsStatus: IconKind =
    loadTestsCompleted && totalTests > 0
      ? unanalyzed === 0
        ? 'completed'
        : testsBusy
          ? 'in_progress'
          : 'paused'
      : 'pending';
  const testsDetail = totalTests > 0 ? `${totalTests} - ${unanalyzed} unscored` : '0';

  // Inputs toggle (Images / Video / Both). Default = whatever exists on
  // disk. If both are present, default to "both" so behaviour matches the
  // server's auto-include logic. Persisted in the run store so the
  // per-test analyze buttons (SidebarTestsList / TestDetailPane) see the
  // same choice the user picked on this toolbar.
  const analysisInputs = useRunStore((s) => s.analysisInputs);
  const setAnalysisInputs = useRunStore((s) => s.setAnalysisInputs);
  // Start from either the user's saved choice or sensible defaults driven
  // by what's actually on disk. Then clamp: if the saved choice references a
  // source that doesn't exist yet, drop it so we never end up with no
  // active pill highlighted.
  const rawInputs: AnalysisInputs =
    analysisInputs ??
    ({ images: analysisExists, video: videoAnalysisExists, provider: 'gemini-native' } as AnalysisInputs);
  const selectedVideoProvider: VideoAnalysisProvider =
    rawInputs.provider ?? 'gemini-native';
  const effectiveInputs: AnalysisInputs = {
    images: rawInputs.images && analysisExists,
    video: rawInputs.video && videoAnalysisExists,
    provider: selectedVideoProvider,
  };
  // If clamping wiped out every source but at least one exists, fall back
  // to whichever one we have so the user always sees a selected pill.
  if (!effectiveInputs.images && !effectiveInputs.video) {
    if (videoAnalysisExists) effectiveInputs.video = true;
    else if (analysisExists) effectiveInputs.images = true;
  }
  const inputsMode: 'images' | 'video' | 'both' =
    effectiveInputs.images && effectiveInputs.video
      ? 'both'
      : effectiveInputs.video
        ? 'video'
        : 'images';
  const onInputsChange = (mode: 'images' | 'video' | 'both') => {
    const next: AnalysisInputs =
      mode === 'images'
        ? { images: true, video: false, provider: selectedVideoProvider }
        : mode === 'video'
          ? { images: false, video: true, provider: selectedVideoProvider }
          : { images: true, video: true, provider: selectedVideoProvider };
    setAnalysisInputs(next);
  };

  return (
    <div className="phase3-toolbar">
      <div className="phase3-toolbar-row">
        <div className="phase3-toolbar-title">
          <span className="phase3-toolbar-eyebrow">Phase 7</span>
          <span
            className="phase3-toolbar-heading"
            title="QAmlet compares the extracted source against the Game-Help screenshots and the per-test steps, then suggests Pass / Fail / N/A / Needs in-game verification for each Xray test."
          >
            TEXEC execution
          </span>
        </div>

        <div className="phase3-toolbar-chips">
          <Phase3Chip
            status={criticalStatus}
            label="Critical"
            detail={criticalDetail}
            title={criticalTooltip}
          />
          <Phase3Chip
            status={screenshotsStatus}
            label="Images"
            detail={screenshotsDetail}
            onClick={imagesReady ? onOpenGallery : onGoToImagesPhase}
            title={screenshotsTooltip}
          />
          <Phase3Chip
            status={videoAnalysisExists ? 'completed' : 'pending'}
            label="Video"
            detail={videoAnalysisExists ? 'analysed' : 'none'}
            onClick={onGoToVideosPage}
            title={
              videoAnalysisExists
                ? 'Gameplay-video analysis is available. Click to open the analyser.'
                : 'No gameplay-video analysis yet. Click to upload a video.'
            }
          />
          <Phase3Chip
            status={testsStatus}
            label="Tests"
            detail={testsDetail}
            title={
              totalTests > 0
                ? `${unanalyzed} unscored of ${totalTests}.`
                : 'Click "Reload Tests" to fetch the test set for this TEXEC.'
            }
          />
          <div
            className="phase3-toolbar-inputs"
            role="radiogroup"
            aria-label="Evidence sources for AI analysis"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '2px',
              fontSize: 12,
              background: 'var(--bg-elev-2)',
            }}
            title="Pick which evidence blocks the AI sees: game-rules images, gameplay-video analysis, or both."
          >
            <span className="muted small" style={{ margin: '0 6px 0 4px' }}>Inputs:</span>
            {(['images', 'video', 'both'] as const).map((mode) => {
              const disabled =
                (mode === 'video' && !videoAnalysisExists) ||
                (mode === 'images' && !analysisExists) ||
                (mode === 'both' && !(analysisExists && videoAnalysisExists));
              const reason = disabled
                ? mode === 'video'
                  ? 'No gameplay-video analysis on disk yet - upload + analyze a video first.'
                  : mode === 'images'
                    ? 'Phase 4 image analysis (analysis_log.txt) has not been run yet.'
                    : 'Both image analysis and video analysis must be present to use Both.'
                : mode === 'images'
                  ? 'AI sees only the game-rules image analysis.'
                  : mode === 'video'
                    ? 'AI sees only the gameplay-video frame analysis.'
                    : 'AI sees image analysis AND video analysis (recommended).';
              const active = inputsMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  className={`phase3-input-pill${active ? ' is-active' : ''}`}
                  onClick={() => onInputsChange(mode)}
                  disabled={disabled}
                  title={reason}
                  aria-pressed={active}
                  style={{
                    background: active ? 'var(--accent)' : 'transparent',
                    color: active ? '#fff' : 'var(--text)',
                    border: active
                      ? '1px solid var(--accent)'
                      : '1px solid transparent',
                    fontWeight: active ? 600 : 400,
                    padding: '3px 10px',
                    borderRadius: 4,
                    opacity: disabled ? 0.35 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'background 80ms ease, color 80ms ease',
                  }}
                >
                  {mode === 'both' ? 'Both' : mode === 'images' ? 'Images' : 'Video'}
                </button>
              );
            })}
            {videoAnalysisExists && (
              <select
                className="input"
                value={selectedVideoProvider}
                onChange={(e) =>
                  setAnalysisInputs({
                    ...effectiveInputs,
                    provider: e.target.value as VideoAnalysisProvider,
                  })
                }
                title="Video evidence provider for Phase 7 and chat prompts"
                style={{ marginLeft: 6, height: 26, fontSize: 12 }}
              >
                <option value="gemini-native">Gemini Native (recommended)</option>
                <option value="openai-frames">OpenAI Frames (legacy)</option>
              </select>
            )}
          </div>
        </div>

        <div className="phase3-toolbar-actions">
          <button
            className="btn small"
            disabled={reloadBusy}
            onClick={onReloadTests}
            title={loadTestsCompleted ? 'Re-load Xray tests' : 'Load Xray tests'}
          >
            {reloadBusy ? '...' : loadTestsCompleted ? 'Reload Tests' : 'Load Tests'}
          </button>
          {!running ? (
            <>
              <button
                className="btn small"
                onClick={() => void start(true, effectiveInputs)}
                disabled={unanalyzed === 0}
                title="Analyze tests with no AI verdict yet"
              >
                Analyze missing ({unanalyzed})
              </button>
              <button
                className="btn btn-primary small"
                onClick={() => void start(false, effectiveInputs)}
                disabled={totalTests === 0}
                title="Re-run the AI on every test"
              >
                Analyze all
              </button>
              <button
                className="btn small"
                onClick={onResetFiltered}
                disabled={resetBusy || filteredScored === 0}
                title="Clear AI/tester/play results for the tests currently visible in the list"
              >
                Reset filtered ({filteredCount})
              </button>
              <button
                className="btn small"
                onClick={onResetAll}
                disabled={resetBusy || scored === 0}
                title="Clear AI/tester/play results for every test. Jira status is kept."
              >
                Reset all AI
              </button>
            </>
          ) : (
            <>
              <span className="phase3-toolbar-progress small">
                <span className="phase3-toolbar-spinner" aria-hidden="true" />
                <span className="muted">
                  {progress
                    ? `${progress.current}/${progress.total} - ${progress.completed} ok - ${progress.errors} errors`
                    : 'Running...'}
                </span>
                {currentlyAnalyzing && (
                  <span
                    className="phase3-toolbar-now"
                    title={`Now analyzing ${currentlyAnalyzing}`}
                  >
                    Now: <strong>{currentlyAnalyzing}</strong>
                  </span>
                )}
              </span>
              <button className="btn btn-danger small" onClick={() => void stop()}>
                Stop
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="phase3-toolbar-error">
          <span style={{ color: 'var(--bad)' }} className="small">{error}</span>
        </div>
      )}
    </div>
  );
}
